import { Capacitor } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { isNativeApp } from '@/utils/platform';
import {
  OFFLINE_ASSET_INDEX_KEY,
  refreshOfflineAssetIndex,
  type OfflineAssetIndex,
} from '@/utils/offlineAssets';

/**
 * The write side of the offline asset store: bytes on disk, and the record of
 * what is there.
 *
 * This module has no opinion about *why* a file is wanted. A look-ahead buffer
 * and an explicit whole-book download both come down to "put this CDN asset on
 * disk for this book", so both are built on the same three operations here:
 * put, has, purge. The scheduling policy — what to fetch next, and when to
 * stop — lives elsewhere.
 *
 * Layout, under `Library/NoCloud` (excluded from iCloud backup):
 *
 *   books/<bookId>/<voiceKey>/<hash>-<basename>   audio and timings
 *   books/<bookId>/shared/<hash>-<basename>       HTML, token maps, CSS, images
 *
 * A book is one directory, so purging it is one `rmdir`. Files keep their
 * original extension because the WebView asset handler chooses the MIME type
 * and the memory-mapped read path from it; the hash of the full CDN URL keeps
 * two assets with the same basename apart.
 *
 * Two records are kept in localStorage:
 *
 * - The **manifest** (this module's own): per asset, where it is relative to
 *   the store root, how many bytes it is, and which book and voice it belongs
 *   to. Usage and purge are answered from it without touching the disk.
 * - The **index** that `resolveAssetUrl` reads (see utils/offlineAssets.ts):
 *   CDN URL -> local URL. It is derived from the manifest on every change and
 *   never edited directly, so the two cannot disagree about what is local.
 *
 * The local URLs in the index embed the app container's absolute path, which
 * iOS may move on an app update. The manifest records the root it was derived
 * against, and `ensureStoreReady` rewrites the index when that root changes,
 * so a relocated container costs one rewrite rather than a silent fallback to
 * streaming for every stored book.
 *
 * Everything here is native-only. On the web there is no disk to write to and
 * `putAsset` rejects; the read-only calls answer as an empty store.
 */

/**
 * Global cap on stored bytes. Roughly two finished full-length books, or many
 * partly-read ones. The store reports when it is reached; deciding what to do
 * about it (evict a finished book, or stop buffering) is policy, not storage.
 */
export const STORE_CAP_BYTES = 2 * 1024 * 1024 * 1024;

/** Directory under `STORE_DIRECTORY` that holds everything this store owns. */
export const STORE_ROOT = 'books';

/**
 * `Library/NoCloud`, not `Library/Caches`: iOS purges Caches under disk
 * pressure, silently and exactly when an offline copy matters. NoCloud is
 * still excluded from backup, which is the property that actually matters —
 * the user never opted in to this data and must not find it in their iCloud
 * backup size.
 */
export const STORE_DIRECTORY = Directory.LibraryNoCloud;

export const OFFLINE_ASSET_MANIFEST_KEY = 'aleph:offline-assets:manifest:v1';

const SHARED_SCOPE = 'shared';

export type AssetStoreErrorKind =
  /** Not running in the native shell. */
  | 'unavailable'
  /** Storing this asset would exceed `STORE_CAP_BYTES`. Nothing was kept. */
  | 'cap'
  /** The file on disk does not match its declared or observed size. Deleted. */
  | 'integrity'
  /** The download itself failed: transport, or a non-2xx response. */
  | 'transfer'
  /** The filesystem or the bookkeeping failed. Nothing was kept. */
  | 'storage'
  /**
   * The book was purged while this download was in flight. Nothing was kept,
   * and nothing is wrong with the asset: ask again if it is still wanted.
   */
  | 'superseded';

export class AssetStoreError extends Error {
  readonly kind: AssetStoreErrorKind;
  /** Set for a `transfer` error that got as far as an HTTP response. */
  readonly httpStatus: number | undefined;

  constructor(
    kind: AssetStoreErrorKind,
    message: string,
    options: { cause?: unknown; httpStatus?: number } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'AssetStoreError';
    this.kind = kind;
    this.httpStatus = options.httpStatus;
  }
}

export const isAssetStoreError = (error: unknown, kind?: AssetStoreErrorKind): error is AssetStoreError =>
  error instanceof AssetStoreError && (kind === undefined || error.kind === kind);

export interface StoredAsset {
  cdnUrl: string;
  bookId: string;
  /** The voice an audio or timings file belongs to; null for shared assets. */
  voiceKey: string | null;
  /** Path relative to `STORE_ROOT`. */
  path: string;
  bytes: number;
  storedAt: number;
}

export interface StoreUsage {
  bytes: number;
  files: number;
  books: number;
  capBytes: number;
  remainingBytes: number;
}

export interface PutAssetOptions {
  cdnUrl: string;
  bookId: string;
  /** Required for audio and timings, which exist per voice; omit for the rest. */
  voiceKey?: string | null;
  /**
   * The size the bundle declared, when it declared one. Used to refuse the
   * download up front if it cannot fit under the cap, and to check the file
   * afterwards. Anything that is not a positive number means "unknown".
   */
  expectedBytes?: number | null;
}

interface Manifest {
  version: 1;
  /** The `file://` URI of `STORE_ROOT` the index's local URLs were derived from. */
  rootUri: string | null;
  assets: Record<string, StoredAsset>;
}

// ---------------------------------------------------------------------------
// Paths

/** Keep a path segment to characters that need no encoding anywhere. */
const sanitizeSegment = (value: string): string => {
  const safe = value.replace(/[^A-Za-z0-9._-]/g, '_').replace(/^\.+/, '_');
  return safe || '_';
};

/** FNV-1a, 32-bit, as eight hex digits. Not cryptographic; it only has to be stable. */
const fnv1a32 = (input: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

/**
 * The file name an asset is stored under: a hash of its full URL, then its
 * basename with the extension intact. Unique per URL, and still readable when
 * looking at the directory on a device.
 */
export const assetFileName = (cdnUrl: string): string => {
  let basename = '';
  try {
    const last = new URL(cdnUrl).pathname.split('/').filter(Boolean).pop() ?? '';
    basename = decodeURIComponent(last);
  } catch {
    basename = '';
  }
  // Keep the tail so the extension survives a very long name.
  const safe = basename ? sanitizeSegment(basename).slice(-120) : '';
  const hash = fnv1a32(cdnUrl);
  return safe ? `${hash}-${safe}` : hash;
};

/** Where an asset lives, relative to `STORE_ROOT`. */
export const assetPath = (bookId: string, voiceKey: string | null | undefined, cdnUrl: string): string =>
  `${sanitizeSegment(bookId)}/${voiceKey ? sanitizeSegment(voiceKey) : SHARED_SCOPE}/${assetFileName(cdnUrl)}`;

/** The same path, relative to `STORE_DIRECTORY`, as the Filesystem plugin wants it. */
const fsPath = (path: string): string => `${STORE_ROOT}/${path}`;

const normalizeRootUri = (uri: string): string => uri.replace(/\/+$/, '');

// ---------------------------------------------------------------------------
// Manifest

let manifestCache: Manifest | null = null;

const emptyManifest = (): Manifest => ({ version: 1, rootUri: null, assets: {} });

const isStoredAsset = (value: unknown): value is StoredAsset => {
  const asset = value as StoredAsset | null;
  return (
    !!asset &&
    typeof asset.cdnUrl === 'string' && asset.cdnUrl.length > 0 &&
    typeof asset.bookId === 'string' && asset.bookId.length > 0 &&
    (asset.voiceKey === null || typeof asset.voiceKey === 'string') &&
    typeof asset.path === 'string' && asset.path.length > 0 &&
    typeof asset.bytes === 'number' && Number.isFinite(asset.bytes) && asset.bytes >= 0 &&
    typeof asset.storedAt === 'number'
  );
};

const readManifest = (): Manifest => {
  try {
    const raw = localStorage.getItem(OFFLINE_ASSET_MANIFEST_KEY);
    if (!raw) {
      return emptyManifest();
    }
    const parsed = JSON.parse(raw) as Partial<Manifest> | null;
    if (!parsed || parsed.version !== 1 || typeof parsed.assets !== 'object' || !parsed.assets) {
      return emptyManifest();
    }
    // Keep only entries that describe a real file. One bad entry should cost
    // that one asset, not the whole store; a dropped entry is at worst an
    // orphaned file that the next purge removes.
    const assets: Record<string, StoredAsset> = {};
    for (const [cdnUrl, asset] of Object.entries(parsed.assets)) {
      if (isStoredAsset(asset) && asset.cdnUrl === cdnUrl) {
        assets[cdnUrl] = asset;
      }
    }
    return {
      version: 1,
      rootUri: typeof parsed.rootUri === 'string' && parsed.rootUri ? parsed.rootUri : null,
      assets,
    };
  } catch {
    return emptyManifest();
  }
};

const getManifest = (): Manifest => {
  if (manifestCache === null) {
    manifestCache = readManifest();
  }
  return manifestCache;
};

const toLocalUrl = (rootUri: string, path: string): string =>
  Capacitor.convertFileSrc(`${rootUri}/${path}`);

/**
 * Derive the index `resolveAssetUrl` reads from the manifest and hand it over.
 * Called after every manifest change, and only then.
 */
const writeIndex = (manifest: Manifest): void => {
  const entries: Record<string, string> = {};
  if (manifest.rootUri) {
    for (const asset of Object.values(manifest.assets)) {
      entries[asset.cdnUrl] = toLocalUrl(manifest.rootUri, asset.path);
    }
  }
  const index: OfflineAssetIndex = { version: 1, entries };
  try {
    localStorage.setItem(OFFLINE_ASSET_INDEX_KEY, JSON.stringify(index));
  } catch (error) {
    // The files are on disk and the manifest knows about them; the reader just
    // streams until the next successful write. Nothing to do but say so.
    console.warn('[assetStore] Could not write the offline asset index.', error);
  }
  // Without this the reader keeps streaming until the next cold start.
  refreshOfflineAssetIndex();
};

/**
 * Commit a manifest. Returns false when it could not be persisted, in which
 * case nothing else is written either: an index that claims files the
 * manifest will not remember on the next launch is worse than no index.
 */
const persistManifest = (manifest: Manifest): boolean => {
  try {
    localStorage.setItem(OFFLINE_ASSET_MANIFEST_KEY, JSON.stringify(manifest));
  } catch (error) {
    console.warn('[assetStore] Could not write the offline asset manifest.', error);
    return false;
  }
  manifestCache = manifest;
  writeIndex(manifest);
  return true;
};

const forgetAssets = (cdnUrls: readonly string[]): void => {
  if (cdnUrls.length === 0) {
    return;
  }
  const manifest = getManifest();
  const assets = { ...manifest.assets };
  for (const cdnUrl of cdnUrls) {
    delete assets[cdnUrl];
  }
  persistManifest({ ...manifest, assets });
};

// ---------------------------------------------------------------------------
// Filesystem helpers

const unavailable = (): AssetStoreError =>
  new AssetStoreError('unavailable', 'The offline asset store only exists in the native app.');

const rootExists = async (): Promise<boolean> => {
  try {
    await Filesystem.stat({ directory: STORE_DIRECTORY, path: STORE_ROOT });
    return true;
  } catch {
    return false;
  }
};

/**
 * Create the store root if it is not there. Probing first keeps the usual
 * boot silent: the plugin has no create-if-missing, and a mkdir of an
 * existing directory is logged as a native error before it is rejected.
 */
const ensureRootDirectory = async (): Promise<void> => {
  if (await rootExists()) {
    return;
  }
  try {
    await Filesystem.mkdir({ directory: STORE_DIRECTORY, path: STORE_ROOT, recursive: true });
  } catch {
    // Raced into existence, or genuinely uncreatable — getUri below will say.
  }
};

const removeFileQuietly = async (path: string): Promise<void> => {
  try {
    await Filesystem.deleteFile({ directory: STORE_DIRECTORY, path: fsPath(path) });
  } catch {
    // Already gone, or never written.
  }
};

const removeDirQuietly = async (path: string): Promise<void> => {
  try {
    await Filesystem.rmdir({ directory: STORE_DIRECTORY, path, recursive: true });
  } catch {
    // Already gone.
  }
};

const statSize = async (path: string): Promise<number | null> => {
  try {
    const info = await Filesystem.stat({ directory: STORE_DIRECTORY, path: fsPath(path) });
    return typeof info.size === 'number' ? info.size : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Readiness

let readyPromise: Promise<string> | null = null;

/**
 * Create the store root and make sure the index matches where it is.
 *
 * **Call this before anything can resolve an asset URL** — the app's entry
 * point does, on native, before the first render. The index holds absolute
 * paths into the app's data container, and iOS moves that container on an
 * app update (seen on the device pass: every reinstall got a new UUID). A
 * reader that resolves a URL before this has run is handed a path that no
 * longer exists, and fails instead of streaming.
 *
 * The root is created explicitly, before anything else, because the
 * filesystem plugin sets `isExcludedFromBackup` on `Library/NoCloud` with a
 * swallowed `try?` that silently does nothing while the directory is absent.
 * Once it exists, every later call through the plugin re-applies the flag.
 *
 * On success the index is always rewritten from the manifest, which is cheap
 * and repairs one that was lost or half-written. On failure the index is
 * emptied: streaming is always correct, a dead local URL never is.
 *
 * Resolves to the `file://` URI of the root. Memoised; a failure clears the
 * memo so the next call tries again.
 */
export const ensureStoreReady = (): Promise<string> => {
  if (!isNativeApp()) {
    return Promise.reject(unavailable());
  }
  if (!readyPromise) {
    readyPromise = (async () => {
      await ensureRootDirectory();
      const { uri } = await Filesystem.getUri({ directory: STORE_DIRECTORY, path: STORE_ROOT });
      const rootUri = normalizeRootUri(uri);
      const manifest = getManifest();
      if (manifest.rootUri !== rootUri) {
        // First run, or the app container moved. Either way every local URL
        // the index holds is derived from the wrong place.
        persistManifest({ ...manifest, rootUri });
      } else {
        writeIndex(manifest);
      }
      return rootUri;
    })().catch((error: unknown) => {
      readyPromise = null;
      console.warn('[assetStore] Could not verify the store root; the reader will stream.', error);
      writeIndex({ ...getManifest(), rootUri: null });
      throw error instanceof AssetStoreError
        ? error
        : new AssetStoreError('storage', 'Could not prepare the offline asset store.', { cause: error });
    });
  }
  return readyPromise;
};

/** The `file://` URI of the store root, for diagnostics. */
export const getStoreRootUri = (): Promise<string> => ensureStoreReady();

// ---------------------------------------------------------------------------
// Reads

/**
 * Every book's payload lists the app's own reader stylesheets beside its
 * bundle's, so those URLs are shared by all books, and the store files a
 * shared asset under whichever book fetched it first. A stylesheet is
 * therefore no evidence that its book is on this device: counted as one,
 * a listener with a single book is told they have two.
 */
const isStylesheet = (asset: StoredAsset): boolean => /\.css$/i.test(asset.path);

export const getStoreUsage = (): StoreUsage => {
  const assets = isNativeApp() ? Object.values(getManifest().assets) : [];
  const bytes = assets.reduce((sum, asset) => sum + asset.bytes, 0);
  return {
    bytes,
    files: assets.length,
    // Books with something of their own here: text or audio, not only styling.
    books: new Set(assets.filter((asset) => !isStylesheet(asset)).map((asset) => asset.bookId)).size,
    capBytes: STORE_CAP_BYTES,
    remainingBytes: Math.max(0, STORE_CAP_BYTES - bytes),
  };
};

export const getStoredAsset = (cdnUrl: string): StoredAsset | null =>
  (isNativeApp() && cdnUrl ? getManifest().assets[cdnUrl] : undefined) ?? null;

/** Whether the store holds a complete copy of this asset, per its own records. */
export const hasAsset = (cdnUrl: string): boolean => getStoredAsset(cdnUrl) !== null;

export const listStoredAssets = (bookId?: string): StoredAsset[] => {
  if (!isNativeApp()) {
    return [];
  }
  const assets = Object.values(getManifest().assets);
  return bookId === undefined ? assets : assets.filter((asset) => asset.bookId === bookId);
};

// ---------------------------------------------------------------------------
// Downloads

/**
 * Content lengths the transfer plugin reported per URL while a download was
 * in flight. The plugin's listener is global and keyed by URL, so one
 * subscription serves every download.
 */
const observedContentLengths = new Map<string, number>();
let progressSubscription: Promise<unknown> | null = null;

export interface AssetProgress {
  cdnUrl: string;
  bytes: number;
  /** Null when the server did not say how long the file is. */
  contentLength: number | null;
}

const progressListeners = new Set<(progress: AssetProgress) => void>();

const ensureProgressListener = (): void => {
  if (progressSubscription || !isNativeApp()) {
    return;
  }
  progressSubscription = FileTransfer.addListener('progress', (status) => {
    if (status.type !== 'download') {
      return;
    }
    const computable = status.lengthComputable && status.contentLength > 0;
    // The first report carries the length from the response headers. The
    // final one echoes the bytes written and would only ever agree with
    // itself, so it must not overwrite the header value.
    if (computable && !observedContentLengths.has(status.url)) {
      observedContentLengths.set(status.url, status.contentLength);
    }
    const progress: AssetProgress = {
      cdnUrl: status.url,
      bytes: status.bytes,
      contentLength: computable ? status.contentLength : null,
    };
    for (const listener of progressListeners) {
      try {
        listener(progress);
      } catch (error) {
        console.warn('[assetStore] A progress listener threw.', error);
      }
    }
  }).catch((error: unknown) => {
    console.warn('[assetStore] Could not subscribe to download progress.', error);
    progressSubscription = null;
  });
};

/**
 * Be told as bytes arrive for any download in progress. Reports are
 * throttled by the plugin to roughly ten a second per transfer. This is what
 * a watchdog can be built on: the transfer plugin has no way to cancel a
 * download, so a scheduler's only defence against one that never finishes
 * is to notice that nothing has arrived for a while and move on.
 */
export const subscribeToAssetProgress = (listener: (progress: AssetProgress) => void): (() => void) => {
  progressListeners.add(listener);
  ensureProgressListener();
  return () => {
    progressListeners.delete(listener);
  };
};

const isDeclaredSize = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const capReached = (requestedBytes: number, usedBytes: number): AssetStoreError =>
  new AssetStoreError(
    'cap',
    `Storing ${requestedBytes} bytes would exceed the ${STORE_CAP_BYTES} byte cap (${usedBytes} in use).`,
  );

const transferError = (cdnUrl: string, error: unknown): AssetStoreError => {
  const data = (error as { data?: { httpStatus?: unknown } } | null)?.data;
  const httpStatus = typeof data?.httpStatus === 'number' ? data.httpStatus : undefined;
  const detail = error instanceof Error ? error.message : String(error);
  return new AssetStoreError('transfer', `Download failed for ${cdnUrl}: ${detail}`, { cause: error, httpStatus });
};

interface InFlightAsset {
  cdnUrl: string;
  bookId: string;
  startedAt: number;
}

const inFlight = new Map<string, { promise: Promise<StoredAsset>; entry: InFlightAsset }>();

/** Downloads currently in progress, oldest first. For diagnostics. */
export const listInFlightAssets = (): InFlightAsset[] =>
  Array.from(inFlight.values(), ({ entry }) => entry).sort((a, b) => a.startedAt - b.startedAt);

/**
 * Bytes promised to downloads that have not landed yet, so two concurrent
 * puts cannot both pass the preflight and together overshoot the cap.
 */
let reservedBytes = 0;

/**
 * Bumped by every purge, for the book purged or for all of them. The
 * transfer plugin cannot cancel a download, so a purge cannot stop one
 * that is already in flight; without this it would land afterwards and be
 * recorded, putting back part of what the user just removed. A put notes
 * the count when it starts and, if it has moved by the time the file
 * lands, discards the file instead.
 */
let purgeEpochAll = 0;
const purgeEpochByBook = new Map<string, number>();
const purgeEpochFor = (bookId: string): number => purgeEpochAll + (purgeEpochByBook.get(bookId) ?? 0);

const superseded = (cdnUrl: string): AssetStoreError =>
  new AssetStoreError('superseded', `The book was purged while ${cdnUrl} was downloading; the file was discarded.`);

const putAssetUncached = async ({
  cdnUrl,
  bookId,
  voiceKey = null,
  expectedBytes = null,
}: PutAssetOptions): Promise<StoredAsset> => {
  if (!cdnUrl || !bookId) {
    throw new AssetStoreError('storage', 'An asset needs a CDN URL and a book id.');
  }
  const rootUri = await ensureStoreReady();

  const already = getManifest().assets[cdnUrl];
  if (already) {
    return already;
  }

  const declared = isDeclaredSize(expectedBytes) ? expectedBytes : null;
  if (declared !== null) {
    const used = getStoreUsage().bytes + reservedBytes;
    if (used + declared > STORE_CAP_BYTES) {
      throw capReached(declared, used);
    }
  }

  const path = assetPath(bookId, voiceKey, cdnUrl);
  const epoch = purgeEpochFor(bookId);
  const reservation = declared ?? 0;
  reservedBytes += reservation;
  ensureProgressListener();
  observedContentLengths.delete(cdnUrl);

  try {
    // The transfer plugin creates the parent directories itself, downloads to
    // its own temporary file, and moves it into place only on success — so a
    // file at the final path is always a finished transfer, never a partial
    // one, and nothing needs preparing here.
    try {
      await FileTransfer.downloadFile({ url: cdnUrl, path: `${rootUri}/${path}`, progress: true });
    } catch (error) {
      // A purge may have taken the directory from under the transfer; that
      // is not a fault of the asset, and must not be reported as one.
      throw purgeEpochFor(bookId) !== epoch ? superseded(cdnUrl) : transferError(cdnUrl, error);
    }
    if (purgeEpochFor(bookId) !== epoch) {
      await removeFileQuietly(path);
      throw superseded(cdnUrl);
    }

    const size = await statSize(path);
    if (size === null) {
      await removeFileQuietly(path);
      throw new AssetStoreError('storage', `The download of ${cdnUrl} left no readable file.`);
    }

    // Captions are checked structurally when they are parsed; audio has no
    // such check, so the size is all there is. The declared size is the
    // stronger reference; the length the server announced is the fallback.
    const expected = declared ?? observedContentLengths.get(cdnUrl) ?? null;
    if (size <= 0 || (expected !== null && size !== expected)) {
      await removeFileQuietly(path);
      throw new AssetStoreError(
        'integrity',
        `The file for ${cdnUrl} is ${size} bytes; expected ${expected ?? 'more than zero'}.`,
      );
    }

    // A download of unknown size is only checked against the cap now that
    // its size is known. Other in-flight reservations still count.
    const used = getStoreUsage().bytes + (reservedBytes - reservation);
    if (used + size > STORE_CAP_BYTES) {
      await removeFileQuietly(path);
      throw capReached(size, used);
    }

    const asset: StoredAsset = { cdnUrl, bookId, voiceKey, path, bytes: size, storedAt: Date.now() };
    const manifest = getManifest();
    if (!persistManifest({ ...manifest, assets: { ...manifest.assets, [cdnUrl]: asset } })) {
      // A file the store cannot remember is a file it can never purge.
      await removeFileQuietly(path);
      throw new AssetStoreError('storage', `Could not record ${cdnUrl} in the offline asset manifest.`);
    }
    return asset;
  } finally {
    reservedBytes -= reservation;
    observedContentLengths.delete(cdnUrl);
  }
};

/**
 * Download one asset into the store and record it.
 *
 * Resolves to the stored record, immediately if the asset is already held.
 * Concurrent calls for the same URL share one download. Rejects with an
 * `AssetStoreError` whose `kind` says what happened; on any rejection nothing
 * is left on disk or in the records for this asset.
 */
export const putAsset = (options: PutAssetOptions): Promise<StoredAsset> => {
  const pending = inFlight.get(options.cdnUrl);
  if (pending) {
    return pending.promise;
  }
  const promise = putAssetUncached(options).finally(() => {
    inFlight.delete(options.cdnUrl);
  });
  inFlight.set(options.cdnUrl, {
    promise,
    entry: { cdnUrl: options.cdnUrl, bookId: options.bookId, startedAt: Date.now() },
  });
  return promise;
};

// ---------------------------------------------------------------------------
// Verification and removal

/**
 * Check a stored asset against the disk. True when the file is there at the
 * recorded size. Otherwise the record is dropped — and a file of the wrong
 * size deleted — so the reader falls back to streaming rather than playing
 * something truncated.
 */
export const verifyAsset = async (cdnUrl: string): Promise<boolean> => {
  const asset = getStoredAsset(cdnUrl);
  if (!asset) {
    return false;
  }
  const size = await statSize(asset.path);
  if (size === asset.bytes) {
    return true;
  }
  if (size !== null) {
    await removeFileQuietly(asset.path);
  }
  forgetAssets([cdnUrl]);
  return false;
};

export const removeAsset = async (cdnUrl: string): Promise<void> => {
  const asset = getStoredAsset(cdnUrl);
  if (!asset) {
    return;
  }
  await removeFileQuietly(asset.path);
  forgetAssets([cdnUrl]);
};

export interface PurgeOptions {
  /**
   * Assets to leave exactly as they are, file and record: whatever the
   * player and the reader are using right now.
   *
   * A local file is served to the audio element a range at a time, each
   * range opening the file again. Delete the file of the chapter that is
   * playing and nothing happens at once — the element has a few seconds
   * buffered — and then the next range fails and playback stops with a
   * media error. So a purge never takes a file out from under the player;
   * it takes everything else, and the buffer carries on from what is left.
   */
  keepUrls?: Iterable<string>;
}

/** Delete everything stored for one book, except what `keepUrls` names. */
export const purgeBook = async (bookId: string, options: PurgeOptions = {}): Promise<void> => {
  if (!isNativeApp() || !bookId) {
    return;
  }
  await ensureStoreReady();
  purgeEpochByBook.set(bookId, (purgeEpochByBook.get(bookId) ?? 0) + 1);
  const keep = new Set(options.keepUrls ?? []);
  const assets = listStoredAssets(bookId);
  const doomed = assets.filter((asset) => !keep.has(asset.cdnUrl));
  if (doomed.length === assets.length) {
    // Nothing of this book is in use: the whole directory, in one call.
    await removeDirQuietly(fsPath(sanitizeSegment(bookId)));
  } else {
    // Something is: take the rest file by file and leave the directory standing.
    for (const asset of doomed) {
      await removeFileQuietly(asset.path);
    }
  }
  forgetAssets(doomed.map((asset) => asset.cdnUrl));
};

/** Delete everything the store holds, except what `keepUrls` names. */
export const purgeAll = async (options: PurgeOptions = {}): Promise<void> => {
  if (!isNativeApp()) {
    return;
  }
  await ensureStoreReady();
  purgeEpochAll += 1;
  const keep = new Set(options.keepUrls ?? []);
  const assets = listStoredAssets();
  if (!assets.some((asset) => keep.has(asset.cdnUrl))) {
    await removeDirQuietly(STORE_ROOT);
    // The root is gone; recreate it (and re-arm the backup flag) on next use.
    readyPromise = null;
    persistManifest({ ...getManifest(), assets: {} });
    return;
  }
  // Something is in use, so the root stays: book by book instead.
  for (const bookId of new Set(assets.map((asset) => asset.bookId))) {
    await purgeBook(bookId, { keepUrls: keep });
  }
};
